import * as prismic from '@prismicio/client'
import cookie from 'js-cookie'
import { EventEmitter } from './EventEmitter'
import { getPreview } from './getPreview'
import { Router } from './Router'
import { repo_name } from '../config.json'
import { getScrollbarWidth } from './lib/getScrollbarWidth'

import styles from './Html.module.css'
import stageStyles from './components/Stage.module.css'
import speakersStyles from './components/Speakers.module.css'
import anchorStyles from './components/Anchor.module.css'
import { Controller } from './const'
import navigationStyles from './components/Navigation.module.css'
import { StageController } from './components/StageController'
import { SpeakersController } from './components/SpeakersController'
import { NavigationController } from './components/NavigationController'
import { AnchorController } from './components/AnchorController'

export class App extends EventEmitter {
  private readonly parser: DOMParser = new DOMParser()
  private readonly previewRef: string | undefined
  private readonly hasPreview: boolean
  private readonly observer: ResizeObserver
  private readonly scrollPositions = new Map<string, number>()

  private controllers: Controller[] = []
  private isBodyFixed = true
  private needsScrollUpdate = false

  public readonly intersectionObserver: IntersectionObserver
  public readonly router: Router

  public pointerX = 0
  public pointerY = 0

  public scrollY = 0
  public isDesktop = false
  private main: HTMLElement
  public stageController?: StageController

  constructor() {
    super()
    this.update = this.update.bind(this)

    const mq = window.matchMedia('(min-width:1024px)')
    mq.addEventListener('change', () => (this.isDesktop = mq.matches))
    this.isDesktop = mq.matches

    const scrollbarWidth = getScrollbarWidth()

    document.body.style.setProperty('--scrollbar', `${scrollbarWidth}px`)
    this.main = document.querySelector('main') as HTMLElement

    this.router = new Router()
    this.router.on('change', this.onNavigate.bind(this))
    this.intersectionObserver = new IntersectionObserver((entries) => this.emit('intersect', entries), {
      threshold: 0.5,
    })

    const previewCookie = cookie.get(prismic.cookie.preview)
    this.previewRef = previewCookie
    this.hasPreview = previewCookie ? !!JSON.parse(previewCookie)[`${repo_name}.prismic.io`] : false

    this.observer = new ResizeObserver(this.onResize.bind(this))

    this.init()
  }

  async onFontLoad() {
    return new Promise<void>((resolve) => document.fonts.ready.then(() => resolve()))
  }

  async init(): Promise<void> {
    await Promise.all([this.onFontLoad()])

    if (this.hasPreview) {
      const html = await this.loadPage(window.location.pathname)
      if (html) this.updatePage(html)
    }

    const stage = document.querySelector(`.${stageStyles.Main}`) as HTMLElement
    if (stage) {
      this.stageController = new StageController(stage, this)
    }

    this.setControllers()
    await Promise.all([this.stageController?.load(), ...this.controllers.map((controller) => controller.load?.())])

    requestAnimationFrame(this.update)
    document.body.addEventListener('click', this.onDocumentClick.bind(this))
    document.body.addEventListener('pointermove', this.onPointerMove.bind(this))
    window.addEventListener('scroll', this.onScroll.bind(this))

    this.observer.observe(document.body)
    document.body.classList.add(styles.Visible)
    this.stageController?.show()
    this.controllers.forEach((controller) => controller.show?.(true))
    this.unsetBodyFixed()
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  }

  onPointerMove(event: PointerEvent) {
    this.pointerX = event.x
    this.pointerY = event.y
  }

  onDocumentClick(event: MouseEvent): void {
    let target: HTMLElement = event.target as HTMLElement

    while (target && target.parentNode) {
      if (target.tagName === 'A') {
        const { origin, pathname, hash, search } = new URL((target as HTMLAnchorElement).href)
        console.log(pathname, hash)
        if (origin === window.location.origin && !(hash && pathname === window.location.pathname)) {
          event.preventDefault()
          this.router.push(pathname, hash, search)
        }
        break
      }
      target = target.parentNode as HTMLElement
    }
  }

  async onNavigate(route: string): Promise<void> {
    this.setBodyFixed()

    const [html] = await Promise.all([
      this.loadPage(route),
      ...this.controllers.map((controller) => controller.hide?.()).filter(Boolean),
    ])

    this.controllers.forEach((controller) => controller.dispose?.())

    this.updatePage(html)

    this.setControllers()

    await Promise.all([...this.controllers.map((controller) => controller.load?.())])
    this.stageController?.updateConfigs()

    this.onResize()

    this.controllers.forEach((controller) => controller.show?.(false))

    this.unsetBodyFixed()

    const scrollPos = this.scrollPositions.get(this.router.activeRoute) || 0
    window.scrollTo(0, scrollPos)
    window.dispatchEvent(new HashChangeEvent('hashchange'))
    this.stageController?.scroll()
  }

  async loadPage(route?: string): Promise<string> {
    if (this.hasPreview && this.previewRef) {
      return await getPreview(this.previewRef, route)
    } else {
      const response = await fetch(`${route}index.html`)
      return await response.text()
    }
  }

  updatePage(html: string): void {
    const doc = this.parser.parseFromString(html, 'text/html')
    const mainNode = doc.querySelector('main') as HTMLElement

    document.title = doc.title
    document.documentElement.lang = doc.documentElement.lang

    window.scrollTo(0, 0)
    this.scrollY = 0

    this.main?.replaceWith(mainNode)
    this.main = document.querySelector('main') as HTMLElement
  }

  setControllers(): void {
    this.controllers = [
      ...this.getNodes(speakersStyles.Main).map((node) => new SpeakersController(node, this)),
      ...this.getNodes(navigationStyles.Main).map((node) => new NavigationController(node, this)),
      ...this.getNodes(anchorStyles.Main).map((node) => new AnchorController(node)),
    ]
  }

  getNodes(className: string): HTMLElement[] {
    return Array.from(document.body?.querySelectorAll(`.${className}`) as NodeListOf<HTMLElement>)
  }

  setBodyFixed(): void {
    if (this.isBodyFixed) return
    this.isBodyFixed = true
    this.scrollY = window.scrollY
  }

  unsetBodyFixed(): void {
    if (!this.isBodyFixed) return
    this.isBodyFixed = false
    window.scrollTo(0, this.scrollY)
  }

  onResize(): void {
    this.stageController?.resize()
    this.controllers.forEach((controller) => controller.resize?.())
  }

  onScroll(): void {
    this.needsScrollUpdate = true
  }

  update(time: number): void {
    requestAnimationFrame(this.update)

    if (this.needsScrollUpdate && !this.isBodyFixed) {
      this.needsScrollUpdate = false
      this.scrollY = window.scrollY
      this.stageController?.scroll()
      this.controllers.forEach((controller) => controller.scroll?.())
      this.scrollPositions.set(this.router.activeRoute, this.scrollY)
    }

    this.stageController?.update(time)
    this.controllers.forEach((controller) => controller.update?.(time))
  }
}
